// OPTIONS HEDGING · the trader's TRADE ticket for one Scanner pair.
// It runs the same backend checks as before, automatically: Evaluate (execution eligibility from the
// backend's own live data) on open, and Preview sizing (eligibility + the broker's read-only margin
// calculators + planned pairs) whenever an amount is entered - so the trader sees "Eligible / Cannot
// trade", the estimated capital and the planned pairs without pressing engineering buttons. Every
// detailed figure (leg book, the eleven capital lines, the original reason codes) stays under
// "View execution details". Create makes a trade that is started separately on Options Hedging;
// nothing is reserved or sent from here.
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, ChevronDown, ChevronRight, XCircle } from "lucide-react";
import { createWithExpected, usePreview } from "@/lib/usePreview";
import { px } from "@/components/options/OptionsCommon";
import { HedgePreview, LegsTable } from "@/components/options/HedgeEligibility";
import PairValuation, { TargetPlan } from "@/components/options/PairValuation";
import { explain, explainFirst, waitingLine } from "@/lib/optionsText";
import { useAdvanced } from "@/lib/advanced";
import { useOptionsEnvironment } from "@/lib/optionsEnv";
import { targetPercent, targetRupees } from "@/lib/targetValues";

const rs = (v) => (v === null || v === undefined ? "—" : `₹${px(v)}`);
const field = "bg-[var(--term-input)] border border-[var(--term-border-strong)] rounded-sm px-2 py-1 w-full mono text-right";

function Field({ label, children, hint }) {
  return (
    <label className="flex flex-col gap-0.5">
      <span className="text-[10px] text-[var(--term-muted)]">{label}</span>
      {children}
      {hint && <span className="text-[10px] text-[var(--term-muted)]">{hint}</span>}
    </label>
  );
}

export default function TradeTicket({ ceToken, peToken, pairId, pair, defaultMode }) {
  const navigate = useNavigate();
  const [advanced] = useAdvanced();
  const env = useOptionsEnvironment();
  // Allot Funds starts from the scanner's Allot Funds (editable here); THIS value is the trade's
  // allocated fund, which sizes the planned pairs and so the quantity sent to the broker
  const [fund, setFund] = useState(pair?.valuation?.allot_funds ? String(pair.valuation.allot_funds) : "");
  const [target, setTarget] = useState("");
  const [targetPct, setTargetPct] = useState("");
  // Target Profit Rs <-> % on Allot Funds: the SAME helpers and behaviour as the Futures trade settings
  const editTargetRs = (v) => { setTarget(v); if (v === "" || (Number.isFinite(Number(v)) && Number(v) >= 0)) setTargetPct(targetPercent(fund, v)); };
  const editTargetPct = (v) => { setTargetPct(v); setTarget(targetRupees(fund, v)); };
  const [stopLoss, setStopLoss] = useState("");
  const [minOpp, setMinOpp] = useState("");
  const [minOi, setMinOi] = useState("");
  // entry price mode: OFFER buys each leg at its best offer; LTP sends both at their own LTPs (they rest until
  // the market reaches them). Defaults to the Scanner mode the row was opened from.
  const [entryMode, setEntryMode] = useState(defaultMode === "LTP" ? "LTP" : "OFFER");
  const [err, setErr] = useState(null);
  const [changed, setChanged] = useState(null);           // the server's authoritative figures differ (Create)
  const [details, setDetails] = useState(false);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  // display only: the scanner's INDEPENDENT reference IV (ATM-implied / configured) for the fair value
  // Frozen when the ticket opens: the scanner row refreshes every ~2 s and its IV moves with it, which used to
  // change the preview request and re-run it continuously (15 Oct 2026). Display only - never sizes anything.
  const [refIv] = useState(() => {
    const refLeg = pair?.valuation?.ce_val;
    return refLeg?.fair_iv ? { iv: refLeg.fair_iv, strike: pair.valuation.atm_strike, source: refLeg.fair_iv_source } : null;
  });

  const overrides = useCallback(() => {
    const o = {};
    if (minOpp !== "") o.min_opportunity = Number(minOpp);
    if (minOi !== "") o.min_oi_execution = Number(minOi);
    if (target !== "") o.profit_target = Number(target);
    if (stopLoss !== "") o.stop_loss = Number(stopLoss);
    o.entry_price_mode = entryMode;
    return o;
  }, [minOpp, minOi, target, stopLoss, entryMode]);

  // Evaluate (no amount yet) or Preview sizing (amount entered): ONE request per input change, the answer
  // applied only for the inputs on screen (lib/usePreview - replaces the overlapping 20 s re-checks)
  const sized = Number(fund) > 0;
  const pv = usePreview({
    enabled: !!env && !created,
    path: sized ? "/options-hedge/trades/preview" : "/options-hedge/candidates/evaluate",
    body: sized ? { environment: env, ce_token: ceToken, pe_token: peToken, allocated_fund: Number(fund), overrides: overrides(), reference_iv: refIv }
      : { environment: env, ce_token: ceToken, pe_token: peToken, overrides: overrides() },
  });
  const last = pv.lastData;
  // sizing ONLY from the answer for the current inputs; prices may show the previous answer while recalculating
  const pre = sized && pv.data && !pv.stale ? pv.data : null;
  const elig = (pv.data && (sized ? pv.data.eligibility : pv.data)) || (last && (last.eligibility || last)) || null;
  // the inputs on screen have not been checked yet (a background refresh of the SAME inputs is not "busy")
  const busy = pv.stale;
  useEffect(() => { setChanged(null); }, [fund, minOpp, minOi, target, stopLoss, entryMode]);

  const body = () => ({ environment: env, ce_token: ceToken, pe_token: peToken, allocated_fund: Number(fund), overrides: overrides(), pair_id: pairId });
  const create = async (confirmed) => {
    setCreating(true); setErr(null);
    // what the ticket SHOWED (or the server's new figures the user just confirmed)
    const shown = confirmed || (pre?.preview ? { planned_pairs: planned, capital_per_pair: pre.preview.capital_per_pair ?? capital } : null);
    const r = await createWithExpected(body(), shown);
    if (r.trade) { setCreated(r.trade); setChanged(null); }
    else if (r.changed) setChanged(r.changed);
    else setErr(r.error);
    setCreating(false);
  };

  const p = pre?.preview;
  const reasons = pre ? pre.reasons : (elig?.reasons || []);
  const eligible = !!elig?.eligible;
  // WAIT reasons (Spread to Maintain not reached yet, quote not yet confirmed) never block creating or
  // starting: the trade waits. Only HARD reasons block (backend eligibility.split_reasons).
  const hard = elig?.hard_reasons ?? (eligible ? [] : (elig?.reasons || []));
  const waiting = !!elig && !eligible && hard.length === 0;
  const startable = !!pre?.startable;
  const capital = p?.lines?.["8_capital_per_pair"]?.value;
  const planned = p?.planned_pairs;
  const lot = p?.cash_model?.lot_cash_multiplier;
  // the entry prices of the chosen mode: best offers, or LTPs
  const priceKey = entryMode === "LTP" ? "ltp" : "ask";
  const off = { ce: elig?.ce?.[priceKey] ?? pair?.ce?.[priceKey], pe: elig?.pe?.[priceKey] ?? pair?.pe?.[priceKey] };
  const combined = elig?.combined_entry ?? (entryMode === "LTP" ? pair?.LTP?.combined : pair?.OFFER?.combined);
  const opp = elig?.opportunity_entry ?? (entryMode === "LTP" ? pair?.LTP?.opp : pair?.OFFER?.opp);
  const oppLot = p?.opportunity_per_pair ?? (entryMode === "LTP" ? pair?.LTP?.opp_lot : pair?.OFFER?.opp_lot);

  let verdict;
  if (env === null) verdict = { ok: false, text: "Options trading is not enabled for your Zerodha account — ask an admin." };
  else if (err) verdict = { ok: false, text: err };
  else if (pv.error) verdict = { ok: false, text: `Could not check this pair — ${pv.error}`, retry: true };
  else if (!elig) verdict = { ok: null, text: "Checking live prices…" };
  else if (hard.length) verdict = { ok: false, text: `Cannot trade — ${explainFirst(hard)}` };
  else if (!(Number(fund) > 0)) verdict = { ok: true, text: waiting ? `Can trade — it will wait: ${waitingLine(elig.waiting, elig.reasons)}. Enter the amount to allocate.` : "Eligible now — enter the amount to allocate to see capital and pairs" };
  else if (!pre) verdict = { ok: null, text: "Calculating with your latest inputs…" };
  else if (!startable) verdict = { ok: false, text: `Cannot trade — ${explainFirst((reasons || []).filter((r) => !(elig?.wait_reasons || []).includes(r)))}` };
  else if (waiting) verdict = { ok: true, text: `Will start and wait — ${waitingLine(elig.waiting, elig.reasons)}` };
  else verdict = { ok: true, text: "Eligible now" };

  const warnings = (elig?.warnings || []).map((w) => explain(w, { mode: entryMode }));

  if (created) {
    return (
      <div className="term-panel rounded-sm p-3 text-xs space-y-2" data-testid="trade-ticket">
        <div className="flex items-center gap-2 text-[#00C805] font-bold"><CheckCircle2 className="h-4 w-4" /> Trade created · {created.name}</div>
        <div className="text-[var(--term-muted)]">Nothing has been sent to Zerodha. Start it on Options Trading when you are ready.</div>
        <button className="px-3 py-1.5 rounded-sm font-bold bg-[#2563EB] text-white" data-testid="ticket-go-hedging"
          onClick={() => navigate(`/options/hedge?trade=${encodeURIComponent(created.id)}`)}>Go to Hedging</button>
      </div>
    );
  }

  return (
    <div className="term-panel rounded-sm p-3 text-xs space-y-3" data-testid="trade-ticket">
      <div className="flex items-center gap-2 flex-wrap">
        <b className="text-sm">New hedge trade · {pair?.underlying || elig?.underlying} {pair?.expiry || elig?.expiry}</b>
        {env && <span className={`ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${env === "LIVE" ? "bg-[#FF3B30]/15 text-[#FF3B30]" : "bg-[#7c3aed]/20 text-[#7c3aed]"}`}
          data-testid="ticket-env">{env}</span>}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 mono" data-testid="ticket-summary">
        <span>CE {pair?.ce?.strike ?? elig?.ce?.strike} · {priceKey === "ltp" ? "LTP" : "offer"} <b>{px(off.ce)}</b></span>
        <span>PE {pair?.pe?.strike ?? elig?.pe?.strike} · {priceKey === "ltp" ? "LTP" : "offer"} <b>{px(off.pe)}</b></span>
        <span>Strike diff <b>{px(pair?.strike_diff ?? elig?.strike_diff)}</b></span>
        <span>Combined entry <b>{px(combined)}</b></span>
        <span>Opportunity <b className={opp > 0 ? "text-[#00C805]" : ""}>{px(opp)}</b> / unit</span>
        <span>{oppLot !== undefined && oppLot !== null ? <>≈ <b>{rs(oppLot)}</b> / lot</> : ""}</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Field label="Allot Funds ₹"><input className={field} value={fund} onChange={(e) => { setFund(e.target.value); if (targetPct !== "") setTarget(targetRupees(e.target.value, targetPct)); }} data-testid="ticket-fund" placeholder="e.g. 50000" /></Field>
        <Field label="Spread to Maintain (per unit)"><input className={field} value={minOpp} onChange={(e) => setMinOpp(e.target.value)} data-testid="ticket-spread-to-maintain"
          title="Entry sends when Offer Opportunity ≥ this (negative allowed, e.g. -5 accepts a NEAR pair); until then the trade waits. Re-checked on a fresh quote immediately before both orders are sent"
          placeholder={elig?.min_opportunity != null ? String(elig.min_opportunity) : "default"} /></Field>
        <Field label="Target Profit ₹"><input className={field} value={target} onChange={(e) => editTargetRs(e.target.value)} data-testid="ticket-target"
          onBlur={() => { if (target === "" || (Number.isFinite(Number(target)) && Number(target) >= 0)) setTarget(targetRupees(fund, targetPct)); }}
          disabled={!(Number(fund) > 0)} placeholder="off" /></Field>
        <Field label="Target Profit %"><input className={field} value={targetPct} onChange={(e) => editTargetPct(e.target.value)} data-testid="ticket-target-pct"
          disabled={!(Number(fund) > 0)} placeholder="% of Allot Funds" /></Field>
        <Field label="Entry price">
          <select className={field} value={entryMode} onChange={(e) => setEntryMode(e.target.value)} data-testid="ticket-entry-mode"
            title={entryMode === "LTP" ? "Both legs are sent together at their own LTPs. Neither filled in 5 min: both cancelled and retried at fresh LTPs. One filled: the other waits at its price (never cancelled or re-priced; re-placed each session) until filled." : "Each leg is bought at its best offer (fills now or within seconds)."}>
            <option value="OFFER">OFFER (best offer)</option>
            <option value="LTP">LTP (rests at last price)</option>
          </select></Field>
        <Field label="Stop loss ₹ (optional)"><input className={field} value={stopLoss} onChange={(e) => setStopLoss(e.target.value)} data-testid="ticket-stop-loss" placeholder="off" /></Field>
      </div>
      <div className="flex items-center gap-4 mono" data-testid="ticket-sizing">
        <span>Estimated capital <b data-testid="ticket-capital">{capital !== undefined && capital !== null ? `${rs(capital)} / pair` : busy && sized ? "calculating…" : "—"}</b></span>
        <span>Planned pairs <b data-testid="ticket-planned">{planned ?? (busy && sized ? "calculating…" : "—")}</b></span>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <span className={`flex items-center gap-1.5 font-bold ${verdict.ok === true ? "text-[#00C805]" : verdict.ok === false ? "text-[#FF3B30]" : "text-[var(--term-muted)]"}`}
          data-testid="ticket-verdict">
          {verdict.ok === true ? <CheckCircle2 className="h-4 w-4" /> : verdict.ok === false ? <XCircle className="h-4 w-4" /> : null}
          {verdict.text}{busy && verdict.ok !== null ? " · recalculating…" : ""}
          {verdict.retry && <button type="button" className="ml-1 underline font-normal" onClick={pv.retry} data-testid="ticket-retry">Retry</button>}
        </span>
        <button className="ml-auto px-4 py-1.5 rounded-sm font-bold bg-[#2563EB] text-white disabled:opacity-40" data-testid="ticket-create"
          // not disabled during the periodic background refresh (it used to swallow clicks for ~2 s of
          // every refresh); the backend re-checks everything on Create anyway
          disabled={!startable || creating || busy} onClick={() => create()}
          title={busy ? "Wait for the check of your latest inputs" : undefined}>{creating ? "Creating…" : "Create trade"}</button>
      </div>
      {changed && <div className="rounded-sm border border-[#B45309]/50 bg-[#B45309]/10 p-2 space-y-1" data-testid="ticket-changed">
        <div className="font-bold text-[#B45309]">The final check differs from what was shown — nothing has been created</div>
        <div className="mono">Now: {changed.preview?.planned_pairs ?? "—"} pair(s) · {rs(changed.preview?.capital_per_pair)} / pair
          {" "}(shown: {planned ?? "—"} · {capital != null ? rs(capital) : "—"})</div>
        <div className="flex gap-2">
          <button className="px-3 py-1 rounded-sm font-bold bg-[#2563EB] text-white" data-testid="ticket-confirm-changed"
            onClick={() => create(changed.preview)}>Create with these values</button>
          <button className="px-3 py-1 rounded-sm border border-[var(--term-border-strong)]" onClick={() => setChanged(null)}>Cancel</button>
        </div>
      </div>}
      {warnings.length > 0 && <div className="text-[11px] text-[#B45309]" data-testid="ticket-warnings">Note: {warnings.join("; ")}.</div>}
      {pre?.target && <TargetPlan t={pre.target} testPrefix="ticket-target-plan" />}
      {pre?.valuation && <PairValuation v={pre.valuation} title="Pair valuation (current quotes)" testPrefix="ticket-valuation" />}

      <button className="flex items-center gap-1 text-[11px] text-[var(--term-muted)] hover:text-[var(--term-primary)]" onClick={() => setDetails(!details)}
        data-testid="ticket-details-toggle">
        {details ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />} View execution details
      </button>
      {details && (
        <div className="space-y-2 border-t border-[var(--term-border)] pt-2" data-testid="ticket-details">
          <div className="grid grid-cols-2 gap-2 max-w-sm">
            <Field label="Minimum OI (both legs)"><input className={field} value={minOi} onChange={(e) => setMinOi(e.target.value)} placeholder="default" /></Field>
          </div>
          {elig && <>
            <div className="mono">{elig.underlying} {elig.expiry} (DTE {elig.dte}) · spot {px(elig.spot)} · offers {px(elig.combined_offer)} → opportunity {px(elig.opportunity_offer)} (min {px(elig.min_opportunity)})</div>
            <LegsTable ce={elig.ce} pe={elig.pe} />
            {(reasons || []).length > 0 && <div className="text-[#FF3B30] mono" data-testid="ticket-reason-codes">Reason codes: {reasons.join(", ")}</div>}
            {(elig.warnings || []).length > 0 && <div className="text-[#B45309] mono">Warning codes: {elig.warnings.join(", ")}</div>}
          </>}
          {p && <HedgePreview p={p} startable={startable} reasons={pre.reasons} />}
          {pre && !p && <div className="text-[#FF3B30]">Not sized: {pre.reasons.join(", ")} {pre.capital_error || ""}</div>}
          {advanced && p && !startable && p.sizable && (p.planned_pairs || 0) > 0 && (
            <button className="underline text-[11px]" onClick={() => create()} data-testid="ticket-create-anyway"
              title="Advanced: create it now and start it later, when it is eligible (Start re-checks everything)">Create anyway (start later)</button>)}
        </div>
      )}
    </div>
  );
}
